/**
 * Phase F1 — Compliance status chip.
 *
 * Renders a tiny pill summarising the stored compliance snapshot
 * for a single expense. Stays neutral and factual at all times:
 *
 *   pending   -> "Within window — due May 14"
 *   due_soon  -> "Due in 2 days"
 *   overdue   -> "5 days past window"
 *   settled   -> "Settled in 9 days"     (or "Settled"  when settled_at known but no days_to_settle)
 *   no_order  -> "No court order applied"  (subtle grey)
 *   voided    -> nothing (the row already shows "Voided")
 *
 * The component is read-only. It expects `snapshot.outputs` to be
 * present and populated by the backend. Returns null on missing
 * data so the row layout doesn't grow a placeholder.
 */
import React from "react";
import { ShieldCheck, Clock, AlertCircle, CircleDashed } from "lucide-react";

const STATUS_STYLES = {
  settled: {
    cls: "bg-[#D6F0EF] border-[#A7D8DA] text-[#0F5658]",
    Icon: ShieldCheck,
  },
  pending: {
    cls: "bg-[#EEF3F4] border-[#D9E1E3] text-[#3F535A]",
    Icon: Clock,
  },
  due_soon: {
    cls: "bg-[#FBF0DC] border-[#E6CFA2] text-[#6F4E1E]",
    Icon: Clock,
  },
  overdue: {
    cls: "bg-[#F8E4DD] border-[#E0B6A8] text-[#7E3B25]",
    Icon: AlertCircle,
  },
  no_order: {
    cls: "bg-white border-[#E5EAEB] text-[#8094A0]",
    Icon: CircleDashed,
  },
};

function formatDueDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso + "T00:00:00Z");
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

export function ComplianceChip({ snapshot }) {
  if (!snapshot || !snapshot.outputs) return null;
  const out = snapshot.outputs;
  if (out.status === "voided" || !out) return null;

  const style = STATUS_STYLES[out.status] || STATUS_STYLES.pending;
  const Icon = style.Icon;

  let label = "";
  if (out.status === "settled") {
    if (typeof out.days_to_settle === "number") {
      label =
        out.days_to_settle === 0
          ? "Settled same day"
          : `Settled in ${out.days_to_settle} day${out.days_to_settle === 1 ? "" : "s"}`;
    } else {
      label = "Settled";
    }
  } else if (out.status === "overdue") {
    label = `${out.days_overdue} day${out.days_overdue === 1 ? "" : "s"} past window`;
  } else if (out.status === "due_soon") {
    label = `Due ${formatDueDate(out.due_date)}`;
  } else if (out.status === "pending") {
    label = `Within window · due ${formatDueDate(out.due_date)}`;
  } else if (out.status === "no_order") {
    label = "No court order";
  } else {
    return null;
  }

  return (
    <span
      className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 border rounded-sm inline-flex items-center gap-1 ${style.cls}`}
      data-testid={`compliance-chip-${snapshot.snapshot_id || "unknown"}`}
      data-status={out.status}
      title={
        out.due_date
          ? `Reimbursement window: ${snapshot.inputs?.reimbursement_window_days || 14} days · due ${out.due_date}`
          : undefined
      }
    >
      <Icon className="w-2.5 h-2.5" strokeWidth={2} />
      {label}
    </span>
  );
}

export default ComplianceChip;
